import React, { useState, useRef, useEffect } from 'react';
import { COLORS, COLOR_LABELS, COLOR_HEX } from '@/domain/constants/colors';

interface ColorSelectProps {
  value: string;
  onChange: (color: string) => void;
  className?: string;
}

export const ColorSelect: React.FC<ColorSelectProps> = ({ value, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  const selectedLabel = value ? COLOR_LABELS[value] : 'Выберите цвет';
  const selectedColor = value ? COLOR_HEX[value] : null;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-left flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          {selectedColor && (
            <div
              className={`w-4 h-4 rounded-full flex-shrink-0 ${value === 'White' ? 'border border-gray-300 dark:border-gray-600' : ''}`}
              style={{
                background: selectedColor
              }}
            />
          )}
          <span className={value ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>{selectedLabel}</span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
          <button
            type="button"
            onClick={() => handleSelect('')}
            className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-500 dark:text-gray-400"
          >
            Выберите цвет
          </button>
          {COLORS.map((color) => {
            const colorHex = COLOR_HEX[color];
            const isSelected = value === color;

            return (
              <button
                key={color}
                type="button"
                onClick={() => handleSelect(color)}
                className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                  isSelected ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-full flex-shrink-0 ${color === 'White' ? 'border border-gray-300 dark:border-gray-600' : ''}`}
                  style={{
                    background: colorHex
                  }}
                />
                <span className="text-gray-900 dark:text-white">{COLOR_LABELS[color]}</span>
                {isSelected && (
                  <svg
                    className="w-5 h-5 text-indigo-600 dark:text-indigo-400 ml-auto"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

